import { PLAYER_COLORS } from "@/lib/api";
import Coin from "@/components/game/Coin";
import { squareText } from "@/lib/squares";

// Demolition confirmation inside the TURN CONTROLS panel: left = the square and the pieces the
// backend computed (wrapping chips); right = fee, Cancel / Confirm. Closes on cancel or after onConfirm resolves.
export default function DemolishConfirm({ preview, cellKey, gridRows, cell, player, onConfirm, onCancel, busy, error }) {
  const label = squareText(cellKey, gridRows);
  if (!preview && !error) return null;

  const total = preview?.total_cost ?? 0;
  const pieces = preview?.pieces ?? [];
  const canAfford = (player?.money ?? 0) >= total;
  const colorHex = cell?.owner ? PLAYER_COLORS[cell.owner] : "var(--ink-3)";

  return (
    <div data-testid="demolish-modal" role="group" aria-label={`Demolish square ${label}`} className="w-full h-full flex min-h-0" style={{ gap: 12 }}>
      <div className="flex-1 min-w-0 flex flex-col min-h-0" style={{ gap: 3 }}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="shrink-0" style={{ width: 14, height: 14, borderRadius: 4, background: colorHex }} />
          <span className="shrink-0" style={{ fontSize: 15, fontWeight: 700, lineHeight: "18px", color: "var(--ink)" }}>Demolish square {label}?</span>
          <span className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)" }}>{cell?.card_name || (cell?.kind === "cube" ? "Cube stack" : "Tile")}</span>
        </div>
        <div style={{ fontSize: 12, lineHeight: "15px", color: "var(--ink-3)" }}>All pieces on this square are removed.</div>
        {error && (
          <div data-testid="demolish-error" className="line-clamp-3" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--danger)" }}>
            {error}
          </div>
        )}
        {!error && pieces.length > 0 && (
          <div data-testid="demolish-piece-list" className="flex flex-wrap content-start min-h-0 overflow-y-auto" style={{ gap: 4, maxHeight: 44 }}>
            {pieces.map((p, i) => (
              <span key={i} data-testid={`demolish-piece-${i}`} className="inline-flex items-center gap-1.5 whitespace-nowrap" style={{ height: 20, padding: "0 7px", borderRadius: 6, border: "1px solid var(--line)", background: "var(--surface)", fontSize: 11.5 }}>
                <span style={{ fontWeight: 600, color: "var(--ink)" }}>{p.card_name}</span>
                <span style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ink-3)" }}>
                  {!preview?.flat_fee ? `${p.cost} money` : p.kind}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col justify-end" style={{ width: 176, gap: 5 }}>
        {!error && (
          <div className="flex items-center justify-between" style={{ fontSize: 12, color: "var(--ink-2)" }}>
            <span className="label-kicker">{preview?.flat_fee ? "Demolish fee" : "Total cost"}</span>
            <span data-testid="demolish-total-cost" className="inline-flex items-center gap-1" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>
              <Coin size={14} />
              {total} money
            </span>
          </div>
        )}
        {!error && !canAfford && (
          <div className="line-clamp-2" style={{ fontSize: 11.5, lineHeight: "14px", fontWeight: 600, color: "var(--danger)" }}>{"You don't have enough money to demolish this square."}</div>
        )}
        <div className="flex items-center" style={{ gap: 5 }}>
          <button data-testid="demolish-cancel" onClick={onCancel} disabled={busy} className="btn-secondary btn-compact btn-card flex-1">
            Cancel
          </button>
          {!error && (
            <button data-testid="demolish-confirm" onClick={onConfirm} disabled={busy || !canAfford} className="btn-primary btn-compact btn-card flex-1">
              Confirm (−{total})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
